import { useState } from "react";
import {
  IcoBoard,
  IcoCheck,
  IcoChevLeft,
  IcoClose,
  IcoDoc,
  IcoLogout,
  IcoPlus,
  IcoSparkle,
  IcoTag,
  IcoTemplate,
  IcoUsers,
} from "./icons";
import type { Launch, Permissions, SessionUser } from "../types/domain";
import { launchAvatarText } from "../lib/launchAvatar";
import { userDisplayName, userInitials } from "../lib/userAvatar";
import { tokenStore } from "../api/token";

export type NavId =
  | "launches"
  | "board"
  | "tasks"
  | "templates"
  | "launchTypes"
  | "landings"
  | "tags"
  | "taskTypes"
  | "admin"
  | "adminRoles"
  | "tasksAudit";

/** Кто видит кнопку «Новый запуск»: только редактор/админ. Staff читает запуски,
 *  но не создаёт. Viewer тем более. */
function canCreateLaunches(role: string | undefined): boolean {
  return role === "editor" || role === "admin";
}

interface SidebarProps {
  activeNav: NavId;
  activeLaunchId: string | null;
  collapsed: boolean;
  mobileOpen: boolean;
  launches: Launch[];
  me: SessionUser | null;
  onNav: (id: NavId) => void;
  onPickLaunch: (id: string) => void;
  onNewLaunch: () => void;
  onToggleCollapse: () => void;
  onMobileClose: () => void;
}

const RESOURCE_ITEMS: Array<{
  id: NavId;
  label: string;
  Icon: typeof IcoTemplate;
  /** Какое право нужно для отображения. NULL = всегда видно. */
  needs: keyof Permissions | null;
}> = [
  { id: "templates", label: "Шаблоны писем", Icon: IcoTemplate, needs: "templates" },
  { id: "landings", label: "Лендинги", Icon: IcoDoc, needs: "landings" },
  { id: "launchTypes", label: "Типы запусков", Icon: IcoSparkle, needs: "launchTypes" },
  { id: "tags", label: "Типы карточек", Icon: IcoTag, needs: "cardTypes" },
  { id: "taskTypes", label: "Типы задач", Icon: IcoTag, needs: "taskTypes" },
];

/** Есть ли у пользователя право видеть ресурс. Для tasks учитывает scope
 *  `viewOwn` — staff видит «Задачи» в сайдбаре, хотя tasks.view=false. Для
 *  остальных ресурсов смотрим на стандартное `view`. */
function canViewResource(
  perms: Permissions | undefined,
  resource: keyof Permissions,
): boolean {
  if (!perms) return false;
  if (resource === "admin") return false;
  const sub = perms[resource];
  if (sub?.view === true) return true;
  if (resource === "tasks" && sub?.viewOwn === true) return true;
  return false;
}

export function Sidebar({
  activeNav,
  activeLaunchId,
  collapsed,
  mobileOpen,
  launches,
  me,
  onNav,
  onPickLaunch,
  onNewLaunch,
  onToggleCollapse,
  onMobileClose,
}: SidebarProps) {
  const activeLaunches = launches.filter((l) => l.status === "active" || l.status === "draft");
  const visibleCount = launches.filter((l) => l.status !== "archived").length;

  const canSeeLaunches = canViewResource(me?.permissions, "launches");
  const canSeeTasks = canViewResource(me?.permissions, "tasks");
  const visibleResources = RESOURCE_ITEMS.filter((it) =>
    it.needs ? canViewResource(me?.permissions, it.needs) : true,
  );
  const showResourcesSection = visibleResources.length > 0;

  return (
    <aside
      className={
        "sidebar" + (collapsed ? " collapsed" : "") + (mobileOpen ? " mobile-open" : "")
      }
    >
      <button
        type="button"
        className="sidebar-toggle"
        onClick={onToggleCollapse}
        title={collapsed ? "Развернуть" : "Свернуть"}
        aria-label={collapsed ? "Развернуть сайдбар" : "Свернуть сайдбар"}
      >
        <IcoChevLeft size={11} sw={2.2} />
      </button>
      <button
        type="button"
        className="sidebar-mobile-close"
        onClick={onMobileClose}
        aria-label="Закрыть меню"
      >
        <IcoClose size={16} />
      </button>

      <div className="sidebar-brand">
        <img className="brand-mark" src="/icon-192.png" alt="" />
        <div>
          <div className="brand-name">LaunchLab</div>
          <div className="brand-sub">planner</div>
        </div>
      </div>

      <div className="sidebar-section-label">Навигация</div>
      {canSeeLaunches && (
        <div
          className={"nav-item " + (activeNav === "launches" ? "active" : "")}
          onClick={() => onNav("launches")}
          data-tip="Запуски"
        >
          <IcoBoard className="nav-icon" size={16} />
          <span>Запуски</span>
          <span className="nav-badge">{visibleCount}</span>
        </div>
      )}
      {canSeeTasks && (
        <div
          className={"nav-item " + (activeNav === "tasks" ? "active" : "")}
          onClick={() => onNav("tasks")}
          data-tip="Задачи"
        >
          <IcoCheck className="nav-icon" size={16} />
          <span>Задачи</span>
        </div>
      )}

      {canSeeLaunches && activeLaunches.length > 0 && (
        <>
          <div className="sidebar-sub-label">Активные · быстрый доступ</div>
          {activeLaunches.slice(0, 4).map((l) => (
            <div
              key={l.id}
              className={
                "nav-item nav-item-sub " +
                (activeNav === "board" && activeLaunchId === l.id ? "active" : "")
              }
              onClick={() => onPickLaunch(l.id)}
              title={l.name}
              data-tip={l.name}
            >
              <div className="nav-launch-dot" style={{ background: l.color ?? "var(--accent)" }}>
                {launchAvatarText(l)}
              </div>
              <span className="nav-launch-name">{l.name}</span>
              {l.status === "active" && (
                <span className="nav-launch-live" title="Активный">
                  <span className="live-dot" />
                </span>
              )}
            </div>
          ))}
        </>
      )}

      {me && canCreateLaunches(me.role) && (
        <div className="sidebar-new-launch-wrap" style={{ padding: "12px 16px 0" }}>
          <button type="button" className="sidebar-new-launch" onClick={onNewLaunch} title="Новый запуск">
            <IcoPlus size={13} stroke="currentColor" sw={2.2} /> <span>Новый запуск</span>
          </button>
        </div>
      )}

      {showResourcesSection && (
        <>
          <div className="sidebar-section-label" style={{ marginTop: 18 }}>Ресурсы</div>
          {visibleResources.map((it) => (
            <div
              key={it.id}
              className={"nav-item " + (activeNav === it.id ? "active" : "")}
              onClick={() => onNav(it.id)}
              data-tip={it.label}
            >
              <it.Icon className="nav-icon" size={16} />
              <span>{it.label}</span>
            </div>
          ))}
        </>
      )}

      {(me?.permissions?.admin?.manageUsers ||
        me?.permissions?.admin?.manageRoles ||
        me?.permissions?.admin?.viewAudit) && (
        <>
          <div className="sidebar-section-label" style={{ marginTop: 18 }}>Администрирование</div>
          {me?.permissions?.admin?.manageUsers && (
            <div
              className={"nav-item " + (activeNav === "admin" ? "active" : "")}
              onClick={() => onNav("admin")}
              data-tip="Пользователи"
            >
              <IcoUsers className="nav-icon" size={16} />
              <span>Пользователи</span>
            </div>
          )}
          {me?.permissions?.admin?.manageRoles && (
            <div
              className={"nav-item " + (activeNav === "adminRoles" ? "active" : "")}
              onClick={() => onNav("adminRoles")}
              data-tip="Роли и права"
            >
              <IcoTag className="nav-icon" size={16} />
              <span>Роли и права</span>
            </div>
          )}
          {me?.permissions?.admin?.viewAudit && (
            <div
              className={"nav-item " + (activeNav === "tasksAudit" ? "active" : "")}
              onClick={() => onNav("tasksAudit")}
              data-tip="Лог задач"
            >
              <IcoCheck className="nav-icon" size={16} />
              <span>Лог задач</span>
            </div>
          )}
        </>
      )}

      <div className="sidebar-foot">
        <UserAvatar me={me} />
        <div className="foot-meta">
          <b>{me ? userDisplayName(me.name, me.email) : "Загружаем…"}</b>
          <span>{me?.email ?? ""}</span>
        </div>
        {me && <LogoutButton />}
      </div>
    </aside>
  );
}

function LogoutButton() {
  return (
    <button
      type="button"
      className="foot-logout"
      title="Выйти"
      aria-label="Выйти"
      onClick={async () => {
        await tokenStore.logout();
        window.location.href = "/login";
      }}
    >
      <IcoLogout size={16} />
    </button>
  );
}

function UserAvatar({ me }: { me: SessionUser | null }) {
  const [failed, setFailed] = useState(false);
  if (!me) return <div className="foot-user">…</div>;
  const initials = userInitials(me.name, me.email);
  if (me.picture && !failed) {
    return (
      <img
        className="foot-user foot-user-img"
        src={me.picture}
        alt={userDisplayName(me.name, me.email)}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return <div className="foot-user">{initials}</div>;
}
